import { baseApi } from "@/core/api/baseApi";
import { type IAPIResponse, type IDivision } from "@/types";
import type { MediworksSourceFilterBody } from "@/types/mediworksSourceFilter";
import { toUniqueIdNamePairs, type IdName } from "@/shared/lib/idName";

interface IDivisionParams {
  name?: string;
  clientId?: number;
  employerId?: number;
  groupId?: number;
  isActive?: string;
  pageNo?: number;
  pageSize?: number;
}

interface IGlobalSearchParams {
  query: string;
  pageNo?: number;
  pageSize?: number;
}

/** `divisionId` narrows the list to that single division. */
export interface DivisionIdNameParams {
  clientId: number;
  employerId: number;
  groupId?: number;
  divisionId?: number;
}

export const divisionsApi = baseApi.injectEndpoints({
  overrideExisting: false,
  endpoints: (build) => ({
    getDivisions: build.query<IAPIResponse<IDivision>, IDivisionParams>({
      query: (body) => ({
        url: `/mediworks/divisions`,
        method: "POST",
        body,
      }),
    }),
    getDivisionsBySource: build.query<
      IAPIResponse<IDivision>,
      { entityId: number; body: MediworksSourceFilterBody }
    >({
      query: ({ entityId, body }) => ({
        url: `mediworks/division/${entityId}`,
        method: "GET",
        body,
      }),
      transformResponse: (response: {
        data?: IDivision | IDivision[];
        totalCount?: number;
        [key: string]: unknown;
      }): IAPIResponse<IDivision> => {
        const data = response.data;
        const list = Array.isArray(data) ? data : data ? [data] : [];
        return {
          data: list,
          totalCount: response.totalCount ?? list.length,
          pageNo: 1,
          pageSize: list.length || 10,
          currentPage: 1,
          totalPages: list.length ? 1 : 0,
        };
      },
    }),
    searchGlobalDivisions: build.query<IAPIResponse<IDivision>, IGlobalSearchParams>({
      query: ({ query, pageNo = 1, pageSize = 100 }) => ({
        url: `/mediworks/divisions/global-search`,
        method: "GET",
        params: { query, pageNo, pageSize },
      }),
    }),
    listDivisions: build.query<IDivision[], void>({
      query: () => ({
        url: `/mediworks/divisions/fetch-id-name`,
        method: "GET",
      }),
    }),
    /** The divisions under one scope, id and name only. See `getGroupsIdName`. */
    getDivisionsIdName: build.query<IdName[], DivisionIdNameParams>({
      query: ({ clientId, employerId, groupId, divisionId }) => ({
        url: `/mediworks/divisions/id-name`,
        method: "GET",
        params: {
          clientId,
          employerId,
          ...(groupId != null && { groupId }),
          ...(divisionId != null && { divisionId }),
        },
      }),
      transformResponse: (r: unknown) => toUniqueIdNamePairs(r, "divisions", "divisionId"),
    }),
    /**
     * The divisions under several employers, id and name only, merged by id — one id-name call per
     * distinct employer. The platform-wide `fetch-id-name` list is keyed by NAME and collapses
     * same-named rows into one, so it cannot be used to name an id; ids under an employer never collide.
     */
    getDivisionsIdNameForScopes: build.query<IdName[], { clientId: number; employerId: number }[]>({
      queryFn: async (scopes, _api, _extra, fetchWithBQ) => {
        const unique = new Map<string, { clientId: number; employerId: number }>();
        for (const { clientId, employerId } of scopes) {
          const key = `${clientId}:${employerId}`;
          if (!unique.has(key)) unique.set(key, { clientId, employerId });
        }
        // Every employer at once rather than one after another; merged in the order asked, so the
        // first employer still wins an id two of them share.
        const results = await Promise.all(
          [...unique.values()].map(({ clientId, employerId }) =>
            fetchWithBQ({
              url: `/mediworks/divisions/id-name`,
              method: "GET",
              params: { clientId, employerId },
            })
          )
        );
        const byId = new Map<number, IdName>();
        for (const res of results) {
          // One employer failing must not blank every other employer's names.
          if (res.error) continue;
          for (const row of toUniqueIdNamePairs(res.data, "divisions", "divisionId"))
            if (!byId.has(row.id)) byId.set(row.id, row);
        }
        return { data: [...byId.values()] };
      },
    }),
  }),
});

export const {
  useGetDivisionsQuery,
  useGetDivisionsBySourceQuery,
  useSearchGlobalDivisionsQuery,
  useListDivisionsQuery,
  useGetDivisionsIdNameQuery,
  useGetDivisionsIdNameForScopesQuery,
} = divisionsApi;
